import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import VendorDirectoryPermissionGate from "../../components/casamap/admin/VendorDirectoryPermissionGate";
import { VENDOR_DIRECTORY_PERMISSIONS } from "../../auth/casamapaVendorDirectoryPermissions";
import {
  getCredentialDetail,
  submitCredentialForReview,
  verifyCredential,
  rejectCredential,
  revokeCredential,
} from "../../api/clientVendorDirectoryVerificationApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.credentials";

const REJECTION_REASONS = [
  "document_unreadable",
  "identifier_invalid",
  "identifier_not_found",
  "name_mismatch",
  "expired_document",
  "unsupported_credential",
  "issuing_authority_unconfirmed",
  "altered_or_suspicious_document",
  "insufficient_evidence",
  "other",
];

function statusBadgeClass(status) {
  if (status === "verified") return "cvd-badge--published";
  if (["rejected", "revoked", "expired"].includes(status)) return "cvd-badge--suspended";
  if (["pending_review", "submitted"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function credentialTypeLabel(credential) {
  return credential?.credentialTypeName || credential?.credentialTypeKey || credential?.credentialTypeId || "—";
}

function decisionField(row, snakeKey, camelKey) {
  return row?.[camelKey] ?? row?.[snakeKey] ?? null;
}

export default function ClientVendorDirectoryCredentialDetailPage() {
  const { clientViewId, credentialId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { t, locale } = useLocale();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerTone, setBannerTone] = useState("success");
  const [reviewNotes, setReviewNotes] = useState("");
  const [rejectReason, setRejectReason] = useState(REJECTION_REASONS[0]);
  const [revokeReason, setRevokeReason] = useState("");

  const basePath = `${serviciosBase}/credenciales`;

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await getCredentialDetail(clientViewId, credentialId);
      setDetail(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.detail.loadError`));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, credentialId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const showBanner = (message, tone = "success") => {
    setBannerMessage(message);
    setBannerTone(tone);
    window.setTimeout(() => setBannerMessage(""), 5000);
  };

  const runAction = async (action) => {
    setBusy(true);
    try {
      await action();
      showBanner(t(`${COPY_PREFIX}.detail.actionSuccess`));
      await load();
    } catch (error) {
      showBanner(error.message || t(`${COPY_PREFIX}.detail.actionError`), "warning");
    } finally {
      setBusy(false);
    }
  };

  const submit = () =>
    runAction(async () => {
      await submitCredentialForReview(clientViewId, credentialId);
    });

  const verify = () =>
    runAction(async () => {
      await verifyCredential(clientViewId, credentialId, {
        verificationMethod: "credential_document",
        reviewNotes: reviewNotes.trim() || undefined,
      });
    });

  const reject = () =>
    runAction(async () => {
      await rejectCredential(clientViewId, credentialId, { rejectionReason: rejectReason });
    });

  const revoke = () => {
    if (!revokeReason.trim()) {
      showBanner(t(`${COPY_PREFIX}.detail.revokeReasonRequired`), "warning");
      return;
    }
    runAction(async () => {
      await revokeCredential(clientViewId, credentialId, { revocationReason: revokeReason.trim() });
      setRevokeReason("");
    });
  };

  if (loading) {
    return (
      <div className="page-content cvd-admin cvd-credentials">
        <ClientPortalLoading message={t(`${COPY_PREFIX}.detail.loading`)} />
      </div>
    );
  }

  if (errorMessage && !detail?.credential) {
    return (
      <div className="page-content cvd-admin cvd-credentials">
        <ClientPortalError message={errorMessage} onRetry={load} />
      </div>
    );
  }

  const credential = detail?.credential;

  return (
    <div className="page-content cvd-admin cvd-credentials">
      <div className="cvd-admin__header">
        <div>
          <p className="cvd-run-detail__eyebrow">{credentialTypeLabel(credential)}</p>
          <h1 className="cvd-admin__title">{credential?.vendorName || credential?.vendorId}</h1>
          <div className="cvd-run-detail__meta">
            <span className={`cvd-badge ${statusBadgeClass(credential?.credentialStatus)}`}>
              {t(`${COPY_PREFIX}.status.${credential?.credentialStatus}`, {}, credential?.credentialStatus)}
            </span>
            {credential?.isPublic ? (
              <span className="cvd-badge cvd-badge--draft">{t(`${COPY_PREFIX}.publicBadge`)}</span>
            ) : null}
          </div>
        </div>
        <div className="cvd-admin__actions">
          <Link className="cvd-btn cvd-btn--ghost" to={basePath}>
            ← {t(`${COPY_PREFIX}.backToList`)}
          </Link>
        </div>
      </div>

      {bannerMessage ? (
        <div className={`cvd-alert cvd-alert--${bannerTone === "warning" ? "warning" : "success"}`}>{bannerMessage}</div>
      ) : null}

      <section className="cvd-panel">
        <h2>{t(`${COPY_PREFIX}.detail.details`)}</h2>
        <dl className="cvd-dl">
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.vendor`)}</dt>
            <dd>{credential?.vendorName || credential?.vendorId || "—"}</dd>
          </div>
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.type`)}</dt>
            <dd>{credentialTypeLabel(credential)}</dd>
          </div>
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.number`)}</dt>
            <dd>{credential?.credentialNumber || "—"}</dd>
          </div>
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.authority`)}</dt>
            <dd>{credential?.issuingAuthority || "—"}</dd>
          </div>
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.issued`)}</dt>
            <dd>{formatDateTime(credential?.issuedAt, locale)}</dd>
          </div>
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.expires`)}</dt>
            <dd>{formatDateTime(credential?.expiresAt, locale)}</dd>
          </div>
          <div>
            <dt>{t(`${COPY_PREFIX}.detail.evidence`)}</dt>
            <dd>{detail?.evidenceCount ?? 0}</dd>
          </div>
        </dl>
      </section>

      <section className="cvd-panel cvd-credential-actions">
        <h2>{t(`${COPY_PREFIX}.detail.actions`)}</h2>

        <label className="cvd-field" htmlFor="review-notes">
          {t(`${COPY_PREFIX}.detail.reviewNotes`)}
          <textarea
            id="review-notes"
            rows={3}
            value={reviewNotes}
            onChange={(event) => setReviewNotes(event.target.value)}
            placeholder={t(`${COPY_PREFIX}.detail.reviewNotesPlaceholder`)}
            disabled={busy}
          />
        </label>

        <div className="cvd-credential-actions__primary">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CREDENTIALS_UPDATE}>
            <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={submit}>
              {t(`${COPY_PREFIX}.detail.submit`)}
            </button>
          </VendorDirectoryPermissionGate>
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CREDENTIALS_VERIFY}>
            <button type="button" className="cvd-btn cvd-btn--primary" disabled={busy} onClick={verify}>
              {t(`${COPY_PREFIX}.detail.verify`)}
            </button>
          </VendorDirectoryPermissionGate>
        </div>

        <div className="cvd-credential-actions__secondary">
          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CREDENTIALS_VERIFY}>
            <div className="cvd-credential-actions__form-row">
              <label className="cvd-field" htmlFor="reject-reason">
                {t(`${COPY_PREFIX}.detail.rejectReason`)}
                <select
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(event) => setRejectReason(event.target.value)}
                  disabled={busy}
                >
                  {REJECTION_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {t(`${COPY_PREFIX}.rejectionReason.${reason}`)}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="cvd-btn cvd-btn--secondary" disabled={busy} onClick={reject}>
                {t(`${COPY_PREFIX}.detail.reject`)}
              </button>
            </div>
          </VendorDirectoryPermissionGate>

          <VendorDirectoryPermissionGate permission={VENDOR_DIRECTORY_PERMISSIONS.CREDENTIALS_VERIFY}>
            <div className="cvd-credential-actions__form-row">
              <label className="cvd-field" htmlFor="revoke-reason">
                {t(`${COPY_PREFIX}.detail.revokeReason`)}
                <input
                  id="revoke-reason"
                  type="text"
                  value={revokeReason}
                  onChange={(event) => setRevokeReason(event.target.value)}
                  placeholder={t(`${COPY_PREFIX}.detail.revokeReasonPlaceholder`)}
                  disabled={busy}
                />
              </label>
              <button type="button" className="cvd-btn cvd-btn--ghost" disabled={busy} onClick={revoke}>
                {t(`${COPY_PREFIX}.detail.revoke`)}
              </button>
            </div>
          </VendorDirectoryPermissionGate>
        </div>
      </section>

      {detail?.decisions?.length ? (
        <section className="cvd-panel">
          <h2>{t(`${COPY_PREFIX}.detail.decisionHistory`)}</h2>
          <ul className="cvd-credential-history">
            {detail.decisions.map((row) => {
              const decision = decisionField(row, "decision", "decision");
              const newStatus = decisionField(row, "new_status", "newStatus");
              const reviewedAt = decisionField(row, "reviewed_at", "reviewedAt") || decisionField(row, "created_at", "createdAt");
              return (
                <li key={row.id}>
                  <strong>
                    {t(`${COPY_PREFIX}.decision.${decision}`, {}, decision)} →{" "}
                    {t(`${COPY_PREFIX}.status.${newStatus}`, {}, newStatus)}
                  </strong>
                  <span className="muted">{formatDateTime(reviewedAt, locale)}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
